import { Alert, AlertDescription, AlertIcon, AlertTitle, Stack } from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import type { OrganisationIdentity } from '@duke-hq/svc-api/admin/trpc/routers/organisation/schemas';

/**
 * The fields whose absence a recipient can see, wherever the organisation is
 * identified. Warning about the rest would nag an organisation that legitimately
 * has no vanity number.
 */
const NOTICEABLE_WHEN_MISSING: Array<[keyof OrganisationIdentity, () => string]> = [
  ['logoUrl', () => t`logo`],
  ['rtoCode', () => t`RTO code`],
  ['supportEmail', () => t`support email`],
  ['supportPhoneDigits', () => t`support phone`],
];

export function IncompleteIdentityWarning({
  identity,
}: {
  identity: OrganisationIdentity;
}): React.ReactElement | null {
  const missing = NOTICEABLE_WHEN_MISSING.filter(([field]) => identity[field] == null).map(
    ([, label]) => label(),
  );

  if (missing.length === 0) {
    return null;
  }

  return (
    <Alert status="warning" alignItems="flex-start" borderRadius="md">
      <AlertIcon />
      <Stack gap={1}>
        <AlertTitle fontSize="sm">
          <Trans>This organisation's details are incomplete</Trans>
        </AlertTitle>
        <AlertDescription fontSize="sm">
          <Trans>
            Anything left blank is left out rather than shown empty. Recipients are currently not
            seeing: {missing.join(', ')}.
          </Trans>
        </AlertDescription>
      </Stack>
    </Alert>
  );
}
